import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  BrowserRouter as Router,
  Route,
  Link,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import "./App.css";
import { API_URL } from "./Config";

// 定義學生資料的型別
interface Student {
  id: number;
  name: string;
  grade: number; // 年級
  gender: string; // 性別
  present: boolean;
  count: number;
  siblings: number[];
}

const App: React.FC = () => {
  const [students, setStudents] = useState<Student[]>([]);
  const [filterGrade, setFilterGrade] = useState<number | null>(null);
  const [filterGender, setFilterGender] = useState<string | null>(null);
  const navigate = useNavigate();
  
  useEffect(() => {
    axios.get<Student[]>(`${API_URL}/students.json`).then((res) => {
      const sortedStudents = sortStudents(res.data); // 先排序
      setStudents(sortedStudents); // 設定排序後的學生資料
    });
  }, []);

  const sortStudents = (students: Student[]): Student[] => {
    let sorted: Student[] = [];
    let visited = new Set<number>();

    // 先將有 sibling 的學生添加到 sorted 中
    const addFamily = (student: Student) => {
      if (visited.has(student.id)) return;
      visited.add(student.id);
      sorted.push(student);
      student.siblings.forEach((sibId) => {
        let sibling = students.find((s) => s.id === sibId);
        if (sibling) addFamily(sibling);
      });
    };

    // 先處理有 sibling 的學生
    students.forEach((student) => {
      if (student.siblings.length > 0 && !visited.has(student.id)) {
        addFamily(student);
      }
    });

    // 再處理沒有 sibling 的學生
    students.forEach((student) => {
      if (student.siblings.length === 0 && !visited.has(student.id)) {
        visited.add(student.id);
        sorted.push(student);
      }
    });

    return sorted;
  };

  const handleAttendanceChange = useCallback((student: Student) => {
    setStudents((prevStudents) => {
      const updatedStudents = prevStudents.map((s) =>
        s.id === student.id ? { ...s, present: !s.present } : s
      );
      saveAttendance(updatedStudents);
      return updatedStudents;
    });
  }, []);

  const saveAttendance = (updatedStudents: Student[]) => {
    axios
      .post(`${API_URL}/api/students`, updatedStudents)
      .then(() => console.log("✅ 點名資料已提交！"))
      .catch(() => console.log("❌ 送出失敗，請稍後再試！"));
  };

  const clearAttendance = () => {
    const updatedStudents = students.map((student) => ({
      ...student,
      present: false,
    }));
    setStudents(updatedStudents);
    saveAttendance(updatedStudents);
  };

  const filteredStudents = students.filter(
    (student) =>
      (filterGrade === null || student.grade === filterGrade) &&
      (filterGender === null || student.gender === filterGender)
  );

  // 計算出席學生數量
  const presentCount = students.filter((student) => student.present).length;

  const isSiblingPresent = (student: Student): boolean => {
    return student.siblings.some((sibId) => {
      const sibling = students.find((s) => s.id === sibId);
      return sibling?.present;
    });
  };

  return (
    <div
      style={{
        marginLeft: "30px",
      }}
    >
      <h1>
        點名系統 : {presentCount} / {students.length}
      </h1>
      <div>
        <button
          onClick={() => {
            if (window.confirm("你確定要清空點名紀錄嗎？")) {
              clearAttendance();
            }
          }}
        >
          清空
        </button>
        <button
          onClick={() => {
            if (window.confirm("確定要進入奉獻人員頁面？")) {
              navigate("/dedication", { state: { students } });
            }
          }}
        >
          奉獻人員
        </button>
      </div>
      <div>
        <button onClick={() => setFilterGrade(null)}>全部年級</button>
        {[0, 1, 2, 3, 4, 5, 6].map((grade) => (
          <button key={grade} onClick={() => setFilterGrade(grade)}>
            {grade === 0 ? "幼稚班" : `${grade} 年級`}
          </button>
        ))}
      </div>
      <div>
        <button onClick={() => setFilterGender(null)}>全部性別</button>
        <button onClick={() => setFilterGender("M")}>男</button>
        <button onClick={() => setFilterGender("F")}>女</button>
      </div>
      <div className="grid-container">
        {filteredStudents.map((student) => (
          <div
            key={student.id}
            className={`grid-item ${student.present ? "present" : "absent"}`}
            style={{
              backgroundColor:
                isSiblingPresent(student) && !student.present ? "#66BB6A" : "",
            }}
            onClick={() => handleAttendanceChange(student)}
          >
            {student.name} (
            {student.grade === 0 ? "幼稚班" : `${student.grade} 年級`}{" "}
            {student.gender})
          </div>
        ))}
      </div>
    </div>
  );
};

const Dedication: React.FC = () => {
  const location = useLocation();
  const students: Student[] = location.state?.students || [];
  let checkCount: any = undefined;
  let countStudents: Student[] = [];
  let tmpStudents: Student[] = [];
  const presentStudents: Student[] = students
    .filter((s) => s.present)
    .sort((a, b) => a.count - b.count);
  presentStudents.forEach((p) => {
    if (checkCount === undefined) {
      checkCount = p.count;
    } else if (checkCount !== p.count) {
      const num = 2 - countStudents.length;
      const shuffled = tmpStudents.sort(() => 0.5 - Math.random()); // 隨機排序
      const selectedStudents = shuffled.slice(0, num); // 取前 2 位
      countStudents.push(...selectedStudents);
      tmpStudents = [];
    }
    tmpStudents.push(p);
    if (countStudents.length >= 2) {
      return;
    } else if (presentStudents.length - 1 === presentStudents.indexOf(p)) {
      const num = 2 - countStudents.length;
      const shuffled = tmpStudents.sort(() => 0.5 - Math.random()); // 隨機排序
      const selectedStudents = shuffled.slice(0, num); // 取前 2 位
      countStudents.push(...selectedStudents);
    }
  });

  const updateStudent = async (id: number) => {
    try {
      let response = await axios.put(`${API_URL}/api/students/${id}`);
      if (response.data.success) {
        console.log("✅ 學生更新成功", response.data.student);
      } else {
        console.error("❌ 更新失敗", response.data.error);
      }
    } catch (error) {
      console.error("❌ 伺服器錯誤", error);
    }
  };

  countStudents.forEach((c) => {
    updateStudent(c.id);
  });

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center", // 垂直居中
        alignItems: "center", // 水平居中
        height: "100vh", // 設定父容器的高度為視窗高度，讓其能夠居中
        textAlign: "center", // 使 <h1> 和 <ul> 文字水平居中
      }}
    >
      <h1 style={{ margin: 0, fontSize: "15vh" }}>司獻人員</h1>
      <ul style={{ display: "flex", justifyContent: "center", padding: 0 }}>
        {countStudents.length > 0 ? (
          countStudents.map((s) => (
            <li
              style={{
                margin: "0 30px", // 左右邊距
                listStyleType: "none",
              }}
            >
              <span>
                <h2 style={{ margin: 0, fontSize: "10vh" }}>{s.name}</h2>
              </span>
            </li>
          ))
        ) : (
          <p>❌ 沒有符合條件的學生</p>
        )}
      </ul>
    </div>
  );
};

const AppWrapper: React.FC = () => {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<App />} />
        <Route path="/dedication" element={<Dedication />} />
      </Routes>
    </Router>
  );
};

export default AppWrapper;
